import type { AppManifest, EntityDef } from '@colplat/schema';

/**
 * Which records can be put back, and how to describe one to a person.
 *
 * Here rather than in `services/api` because both sides need the same
 * answers: the server lists and restores, and since the local copy carries
 * deleted rows (ADR-0025, amended 2026-10-06) the client lists them too,
 * offline. Two implementations of "which entities" and "which field names
 * this thing" is how the two quietly come to disagree — the page showing one
 * set of categories and the server accepting another.
 *
 * Derived from the manifest throughout. Adding an entity adds it here.
 */

/**
 * Top-level records only.
 *
 * A child comes back with the parent whose deletion took it. Offering a split
 * on its own would invite putting a leg back into a transaction that is still
 * gone — a half-record nothing else in the app can represent.
 */
export function restorableEntities(manifest: AppManifest): Array<[string, EntityDef]> {
  return Object.entries(manifest.entities).filter(([, def]) => !def.parent);
}

/**
 * The conventional names for "what is this record called", best first.
 *
 * A naming convention rather than a per-entity table: an entity that calls
 * its label `name` needs no change here, and one that calls it something new
 * falls through to the width rule rather than breaking.
 */
const LABEL_FIELDS = ['name', 'description', 'title', 'originalName', 'note', 'pattern', 'label'];

/** Declared width, with a sane default — the proxy for "is this a label or a code". */
function width(f: { max?: number; type: string }): number {
  return f.max ?? (f.type === 'text' ? 1_000_000 : 255);
}

/**
 * Up to three fields that identify a deleted record to the person deciding
 * whether to put it back.
 *
 * Three rules, each of which was wrong on its own. Width alone led an account
 * with its `description`; "required first" led every transaction with "HUF",
 * because `currencyCode` is required and three characters wide while
 * `description` is neither; and without the last filter a bank connection
 * printed its consent `sessionId` into a list.
 */
export function summaryFields(def: EntityDef): string[] {
  const rank = (n: string): number => {
    const i = LABEL_FIELDS.indexOf(n);
    return i === -1 ? LABEL_FIELDS.length : i;
  };

  const labels = Object.entries(def.fields)
    .filter(([, f]) => (f.type === 'string' || f.type === 'text')
      // Not a payload: `icon.data` is a whole image, and a base64 blob has no
      // business in a list of rows nobody has asked to open.
      && width(f) <= 512
      // Not server bookkeeping, and not something the manifest has already
      // said must not reach a device.
      && !f.system && f.replicate !== false)
    .sort(([a, fa], [b, fb]) => rank(a) - rank(b) || width(fb) - width(fa))
    .map(([n]) => n);

  const dated = Object.entries(def.fields)
    .filter(([n, f]) => (f.type === 'date' || f.type === 'datetime') && !f.system && n !== 'created')
    .map(([n]) => n);

  return [...labels, ...dated].slice(0, 3);
}

/** `entity -> the fields that describe it`, for every restorable entity. */
export function summaryFieldsByEntity(manifest: AppManifest): Record<string, string[]> {
  return Object.fromEntries(
    restorableEntities(manifest).map(([name, def]) => [name, summaryFields(def)]),
  );
}

/**
 * Everything one delete removed, worked out from the rows alone.
 *
 * The server knows this exactly: records deleted together carry the same HLC
 * on their `deleted` record events. A device has no `record_events`, so it
 * needs the same grouping expressed in something that replicates — and that
 * is the rows' own `deleted_at`, which `softDeleteRecords` now stamps once
 * per operation rather than once per row.
 *
 * Two conditions, both required. The child must *belong* to the record
 * (`parent.field` points at it), and it must have gone at the same instant.
 * Belonging alone would resurrect a leg edited out of the transaction a month
 * earlier, which was deleted separately and is meant to stay gone; the same
 * instant alone would sweep up whatever else happened to be deleted in the
 * same operation elsewhere.
 *
 * Returns the record itself first. A row whose `deleted_at` does not match —
 * deleted before this rule existed, say — simply is not included, so the
 * worst case is the old behaviour rather than a wrong one.
 */
export function cascadeMembers(
  manifest: AppManifest,
  entity: string,
  recordId: string,
  deletedAt: string,
  rowsOf: (entity: string) => ReadonlyArray<Record<string, unknown>>,
): Array<{ entity: string; recordId: string }> {
  const members = [{ entity, recordId }];
  for (const [childName, def] of Object.entries(manifest.entities)) {
    if (def.parent?.entity !== entity) continue;
    const field = def.parent.field;
    for (const row of rowsOf(childName)) {
      if (row[field] !== recordId) continue;
      if (row['deleted_at'] !== deletedAt) continue;
      members.push({ entity: childName, recordId: String(row['id']) });
    }
  }
  return members;
}
